/**
 * Authentication API service.
 */

import api, { tokenStorage } from "@/services/api";
import type { LoginResponse, MessageResponse, ModuleCode, TokenPair, User } from "@/types";

export interface RegisterPayload {
  email: string;
  password: string;
  full_name: string;
  phone_number: string;
  // User Onboarding + Module Access (Pilot). Always ["OPTION_ANALYST"]
  // today -- see RegisterPage.tsx -- but typed as a list to match the
  // backend contract, which accepts multiple.
  selected_modules: ModuleCode[];
}

export const authService = {
  async register(payload: RegisterPayload): Promise<User> {
    const { data } = await api.post<User>("/auth/register", payload);
    return data;
  },

  async login(email: string, password: string): Promise<LoginResponse> {
    const form = new URLSearchParams();
    form.set("username", email);
    form.set("password", password);

    const { data } = await api.post<LoginResponse>("/auth/login", form, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    tokenStorage.setTokens(data);
    return data;
  },

  async logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await api.post("/auth/logout", { refresh_token: refreshToken });
      } finally {
        tokenStorage.clear();
      }
    } else {
      tokenStorage.clear();
    }
  },

  async refresh(): Promise<TokenPair> {
    const refreshToken = tokenStorage.getRefreshToken();
    const { data } = await api.post<TokenPair>("/auth/refresh-token", {
      refresh_token: refreshToken,
    });
    tokenStorage.setTokens(data);
    return data;
  },

  async me(): Promise<User> {
    const { data } = await api.get<User>("/auth/me");
    return data;
  },

  async verifyEmail(token: string): Promise<MessageResponse> {
    const { data } = await api.post<MessageResponse>("/auth/verify-email", { token });
    return data;
  },
  async resendVerification(email: string): Promise<MessageResponse> {
    const { data } = await api.post<MessageResponse>("/auth/resend-verification", { email });
    return data;
  },
  async forgotPassword(email: string): Promise<void> {
    await api.post("/auth/forgot-password", { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post("/auth/reset-password", { token, new_password: newPassword });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },
};

export default authService;
